import { resolveAppCategory } from "./apps/categoryNormalization";
import { todayKey } from "./format";

/** Snapshot cleanup window in `convex/usage.ts` — not a UI navigation bound. */
export const USAGE_RETENTION_DAYS = 90;

/** Monday–Sunday calendar week in the device's local timezone (same basis as `todayKey`). */
export function parseDateKey(key: string): Date {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function addDays(key: string, days: number): string {
  const date = parseDateKey(key);
  date.setDate(date.getDate() + days);
  return todayKey(date);
}

export function startOfWeek(key: string): string {
  const date = parseDateKey(key);
  const weekday = date.getDay();
  const daysFromMonday = weekday === 0 ? 6 : weekday - 1;
  date.setDate(date.getDate() - daysFromMonday);
  return todayKey(date);
}

export function endOfWeek(key: string): string {
  return addDays(startOfWeek(key), 6);
}

export function startOfMonth(key: string): string {
  const [year, month] = key.split("-").map(Number);
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01`;
}

export function endOfMonth(key: string): string {
  const date = parseDateKey(key);
  // Day 0 of the next month is the last day of this month.
  return todayKey(new Date(date.getFullYear(), date.getMonth() + 1, 0));
}

/** Adds calendar months, clamping the day to the target month's length. */
export function addMonths(key: string, months: number): string {
  const date = parseDateKey(key);
  const monthIndex = date.getFullYear() * 12 + date.getMonth() + months;
  const targetYear = Math.floor(monthIndex / 12);
  const targetMonth = ((monthIndex % 12) + 12) % 12;
  const daysInTarget = new Date(targetYear, targetMonth + 1, 0).getDate();
  const day = Math.min(date.getDate(), daysInTarget);
  return todayKey(new Date(targetYear, targetMonth, day));
}

export function dateRangeInclusive(startKey: string, endKey: string): string[] {
  const dates: string[] = [];
  let cursor = startKey;
  while (cursor <= endKey) {
    dates.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return dates;
}

/** @deprecated UI navigation no longer caps at retention; kept for tests referencing storage policy. */
export function earliestNavigableDate(referenceDate = todayKey()): string {
  return addDays(referenceDate, -(USAGE_RETENTION_DAYS - 1));
}

export function isDateKeyBefore(a: string, b: string): boolean {
  return a < b;
}

export function isDateKeyAfter(a: string, b: string): boolean {
  return a > b;
}

export function isSameMonth(a: string, b: string): boolean {
  return startOfMonth(a) === startOfMonth(b);
}

export function isSameWeek(a: string, b: string): boolean {
  return startOfWeek(a) === startOfWeek(b);
}

export function isDateInWeek(date: string, weekAnchor: string): boolean {
  return isSameWeek(date, weekAnchor);
}

export function isDateInMonth(date: string, monthAnchor: string): boolean {
  return isSameMonth(date, monthAnchor);
}

/** First cell (Monday) of the month grid for `monthKey` (any day in the target month). */
export function monthGridStart(monthKey: string): string {
  return startOfWeek(startOfMonth(monthKey));
}

export function buildMonthGridCells(monthKey: string, weeks = 6): string[] {
  const start = monthGridStart(monthKey);
  return Array.from({ length: weeks * 7 }, (_, index) => addDays(start, index));
}

export function resolveDatePickForMode(
  pickedDay: string,
  mode: "day" | "week" | "month",
): string {
  if (mode === "week") return startOfWeek(pickedDay);
  if (mode === "month") return startOfMonth(pickedDay);
  return pickedDay;
}

export function canNavigateDayPrev(_selectedDate: string, _referenceDate = todayKey()): boolean {
  return true;
}

export function canNavigateDayNext(selectedDate: string, referenceDate = todayKey()): boolean {
  return selectedDate < referenceDate;
}

export function canNavigateWeekPrev(_selectedDate: string, _referenceDate = todayKey()): boolean {
  return true;
}

export function canNavigateWeekNext(selectedDate: string, referenceDate = todayKey()): boolean {
  return addDays(startOfWeek(selectedDate), 7) <= referenceDate;
}

export function canNavigateMonthPrev(_selectedDate: string, _referenceDate = todayKey()): boolean {
  return true;
}

export function canNavigateMonthNext(selectedDate: string, referenceDate = todayKey()): boolean {
  return startOfMonth(selectedDate) < startOfMonth(referenceDate);
}

export function canNavigateCalendarMonthPrev(_visibleMonth: string): boolean {
  return true;
}

export function canNavigateCalendarMonthNext(visibleMonth: string, referenceDate = todayKey()): boolean {
  return startOfMonth(visibleMonth) < startOfMonth(referenceDate);
}

export type AggregatableApp = {
  appIdentifier?: string;
  displayName: string;
  durationSeconds: number;
  category?: string;
  isEstimated?: boolean;
  color?: string;
  opens?: number;
};

export function topAppKey(app: AggregatableApp): string {
  return app.appIdentifier ?? app.displayName;
}

export function mergeTopApps(appLists: AggregatableApp[][]): AggregatableApp[] {
  const merged = new Map<string, AggregatableApp>();
  for (const apps of appLists) {
    for (const app of apps) {
      const key = topAppKey(app);
      const existing = merged.get(key);
      if (existing) {
        existing.durationSeconds += app.durationSeconds;
        existing.category = resolveAppCategory(
          existing.category,
          app.category,
          existing.appIdentifier ?? app.appIdentifier,
        );
        if (app.opens != null) {
          existing.opens = (existing.opens ?? 0) + app.opens;
        }
      } else {
        merged.set(key, { ...app });
      }
    }
  }
  return [...merged.values()].sort((a, b) => b.durationSeconds - a.durationSeconds);
}

export function mergeTimeBands(
  bandLists: Array<Array<{ key: string; seconds: number; opens?: number }>>,
): Array<{ key: string; seconds: number; opens?: number }> {
  const keys = ["morning", "afternoon", "evening", "night"] as const;
  const secondTotals = new Map<string, number>();
  const openTotals = new Map<string, number>();
  for (const bands of bandLists) {
    for (const band of bands) {
      secondTotals.set(band.key, (secondTotals.get(band.key) ?? 0) + band.seconds);
      if (band.opens != null) {
        openTotals.set(band.key, (openTotals.get(band.key) ?? 0) + band.opens);
      }
    }
  }
  return keys.map((key) => ({
    key,
    seconds: secondTotals.get(key) ?? 0,
    opens: openTotals.has(key) ? openTotals.get(key) : undefined,
  }));
}
